import { api } from './api'

export interface StudentStats {
  count: number
}

export interface CollegeCourse {
  id: string
  name: string
  code: string
  isActive: boolean
  studentCount: number
}

export interface YearWiseFee {
  yearNumber: number
  feeAmount: number
}

export interface CourseGroupForEnrollment {
  id: string
  name: string
  code: string
  totalFee: number
  durationYears: number
  description?: string
  formattedFee: string
  yearWiseFees: YearWiseFee[]
}

export interface StudentFeeStructure {
  yearNumber: number
  feeAmount: number
  discountAmount: number
  finalAmount: number
  academicYear: string
  formattedFeeAmount: string
  formattedDiscountAmount: string
  formattedFinalAmount: string
}

export interface StudentFeeStructureResponse {
  student: {
    id: string
    studentId: string
    rollNumber: string
    firstName: string
    lastName: string
    fatherName: string
    college: {
      name: string
      code: string
    }
    courseGroup: {
      name: string
      code: string
      durationYears: number
    }
  }
  feeStructure: StudentFeeStructure[]
  totals: {
    totalFeeAmount: number
    totalDiscountAmount: number
    totalFinalAmount: number
  }
}

export interface StudentFilterInfo {
  courseGroups: Array<{
    id: string
    name: string
    code: string
  }>
  batchYears: string[]
  enrollmentStatuses: string[]
  colleges?: Array<{
    id: string
    name: string
    code: string
  }>
}

export interface StudentStatsQuery {
  startDate?: string
  endDate?: string
  isActive?: boolean
  academicYear?: string
  batchYear?: string
}

export const studentService = {
  // Get student statistics
  async getStudentStats(query: StudentStatsQuery = {}): Promise<StudentStats> {
    const params = new URLSearchParams()
    
    if (query.startDate) params.append('startDate', query.startDate)
    if (query.endDate) params.append('endDate', query.endDate)
    if (query.isActive !== undefined) params.append('isActive', query.isActive.toString())
    if (query.academicYear) params.append('academicYear', query.academicYear)
    if (query.batchYear) params.append('batchYear', query.batchYear)

    const response = await api.get(`/students/stats?${params.toString()}`)
    return response.data.data
  },

  // Get college courses with student count
  async getCollegeCourses(): Promise<CollegeCourse[]> {
    const response = await api.get('/students/courses')
    return response.data.data
  },

  // Get total student count (active)
  async getTotalActiveStudents(): Promise<StudentStats> {
    return this.getStudentStats({ isActive: true })
  },

  // Get new students this month
  async getNewStudentsThisMonth(): Promise<StudentStats> {
    const now = new Date()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    
    return this.getStudentStats({
      startDate: startOfMonth.toISOString().split('T')[0],
      endDate: endOfMonth.toISOString().split('T')[0]
    })
  },

  // Get student filter information (courses, batch years, statuses)
  async getStudentFilterInfo(): Promise<StudentFilterInfo> {
    const response = await api.get('/students/info')
    return response.data.data
  },

  // Get student by ID
  async getStudentById(id: string): Promise<any> {
    const response = await api.get(`/students/${id}`)
    return response.data.data
  },

  // Update student
  async updateStudent(id: string, data: any): Promise<any> {
    const response = await api.put(`/students/${id}`, data)
    return response.data.data
  },

  // Get course groups for enrollment with fee information
  async getCourseGroupsForEnrollment(): Promise<CourseGroupForEnrollment[]> {
    const response = await api.get('/students/enrollment/course-groups')
    return response.data.data
  },

  // Delete student
  async deleteStudent(id: string): Promise<void> {
    await api.delete(`/students/${id}`)
  },

  // Create new student
  async createStudent(data: any): Promise<any> {
    const response = await api.post('/students', data)
    return response.data.data
  },

  // Get student fee structure
  async getStudentFeeStructure(id: string): Promise<StudentFeeStructureResponse> {
    const response = await api.get(`/students/${id}/fee-structure`)
    return response.data.data
  }
}
